import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators
} from '@angular/forms';
import {
  Component,
  OnInit
} from '@angular/core';
import { Router } from '@angular/router';

interface CategoryOption {
  id: number;
  name: string;
}

interface SubcategoryPayload {
  categoryId: number;
  name: string;
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
  ogImage: File | null;
}

@Component({
  selector: 'app-add-sub-category',
  standalone: false,
  templateUrl: './add-sub-category.html',
  styleUrl: './add-sub-category.scss',
})
export class AddSubCategory  implements OnInit{
  pageTitle = 'Add New Subcategory';

  pageSubtitle =
    'Create and organize a new course subcategory.';

  subcategoryForm!: FormGroup;

  isSubmitted = false;
  isSaving = false;

  ogImage: File | null = null;
  ogImagePreview = '';
  ogImageError = '';

  private saveAnotherAfterSubmit = false;

  private readonly allowedOgImageTypes: string[] = [
    'image/jpeg',
    'image/png'
  ];

  private readonly maximumOgImageSize =
    2 * 1024 * 1024;

  readonly categories: CategoryOption[] = [
    {
      id: 1,
      name: 'Finance & Accounting'
    },
    {
      id: 2,
      name: 'Business'
    },
    {
      id: 3,
      name: 'Development'
    },
    {
      id: 4,
      name: 'IT & Software'
    },
    {
      id: 5,
      name: 'Design'
    },
    {
      id: 6,
      name: 'Personal Development'
    },
    {
      id: 7,
      name: 'Marketing'
    },
    {
      id: 8,
      name: 'Photography & Video'
    },
    {
      id: 9,
      name: 'Health & Fitness'
    },
    {
      id: 10,
      name: 'Music'
    },
    {
      id: 11,
      name: 'Teaching & Academics'
    },
    {
      id: 12,
      name: 'Office Productivity'
    }
  ];

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.initializeForm();
  }

  get categoryControl(): AbstractControl {
    return this.subcategoryForm.get(
      'categoryId'
    ) as AbstractControl;
  }

  get nameControl(): AbstractControl {
    return this.subcategoryForm.get(
      'name'
    ) as AbstractControl;
  }

  get metaTitleControl(): AbstractControl {
    return this.subcategoryForm.get(
      'metaTitle'
    ) as AbstractControl;
  }

  get metaDescriptionControl(): AbstractControl {
    return this.subcategoryForm.get(
      'metaDescription'
    ) as AbstractControl;
  }

  get metaKeywordsControl(): AbstractControl {
    return this.subcategoryForm.get(
      'metaKeywords'
    ) as AbstractControl;
  }

  private initializeForm(): void {
    this.subcategoryForm =
      this.formBuilder.group({
        categoryId: [
          '',
          [
            Validators.required
          ]
        ],
        name: [
          '',
          [
            Validators.required,
            Validators.maxLength(100)
          ]
        ],
        metaTitle: [
          '',
          [
            Validators.maxLength(100)
          ]
        ],
        metaDescription: [
          '',
          [
            Validators.maxLength(300)
          ]
        ],
        metaKeywords: [
          '',
          [
            Validators.maxLength(250)
          ]
        ]
      });
  }

  onOgImageSelected(event: Event): void {
    const input =
      event.target as HTMLInputElement;

    const file =
      input.files?.[0];

    this.ogImageError = '';

    if (!file) {
      return;
    }

    if (
      !this.allowedOgImageTypes.includes(
        file.type
      )
    ) {
      this.clearOgImage(input);

      this.ogImageError =
        'Only JPEG, JPG and PNG images are allowed.';

      return;
    }

    if (
      file.size >
      this.maximumOgImageSize
    ) {
      this.clearOgImage(input);

      this.ogImageError =
        'The OG image must not exceed 2 MB.';

      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      if (
        typeof reader.result === 'string'
      ) {
        this.ogImage = file;
        this.ogImagePreview =
          reader.result;
      }
    };

    reader.onerror = () => {
      this.clearOgImage(input);

      this.ogImageError =
        'The OG image could not be read.';
    };

    reader.readAsDataURL(file);
  }

  submitSubcategory(): void {
    this.saveSubcategory(false);
  }

  saveAndAddAnother(): void {
    this.saveSubcategory(true);
  }

  cancel(): void {
    window.history.back();
  }

  trackByCategoryId(
    _index: number,
    category: CategoryOption
  ): number {
    return category.id;
  }

  private saveSubcategory(
    saveAnother: boolean
  ): void {
    this.isSubmitted = true;
    this.saveAnotherAfterSubmit =
      saveAnother;

    if (
      this.subcategoryForm.invalid ||
      this.ogImageError ||
      this.isSaving
    ) {
      this.subcategoryForm.markAllAsTouched();
      return;
    }

    const formValue =
      this.subcategoryForm.getRawValue();

    const payload: SubcategoryPayload = {
      categoryId: Number(
        formValue.categoryId
      ),
      name:
        formValue.name.trim(),
      metaTitle:
        formValue.metaTitle.trim(),
      metaDescription:
        formValue.metaDescription.trim(),
      metaKeywords:
        formValue.metaKeywords.trim(),
      ogImage:
        this.ogImage
    };

    const formData = new FormData();

    formData.append(
      'categoryId',
      String(payload.categoryId)
    );

    formData.append(
      'name',
      payload.name
    );

    formData.append(
      'metaTitle',
      payload.metaTitle
    );

    formData.append(
      'metaDescription',
      payload.metaDescription
    );

    formData.append(
      'metaKeywords',
      payload.metaKeywords
    );

    if (payload.ogImage) {
      formData.append(
        'ogImage',
        payload.ogImage
      );
    }

    this.isSaving = true;

    console.log(
      'Subcategory payload:',
      payload
    );

    console.log(
      'Subcategory form data:',
      formData
    );

    /*
    Replace the timeout with your API:

    this.subcategoryService
      .createSubcategory(formData)
      .pipe(
        finalize(() => {
          this.isSaving = false;
        })
      )
      .subscribe({
        next: () => {
          this.handleSaveSuccess();
        },
        error: (error) => {
          console.error(
            'Subcategory creation failed:',
            error
          );
        }
      });
    */

    setTimeout(() => {
      this.isSaving = false;
      this.handleSaveSuccess();
    }, 800);
  }

  private handleSaveSuccess(): void {
    this.isSubmitted = false;

    if (this.saveAnotherAfterSubmit) {
      this.resetForm();
      return;
    }

    this.router.navigate([
      '/admin/course-reference/subcategory'
    ]);
  }

  private resetForm(): void {
    this.subcategoryForm.reset({
      categoryId: '',
      name: '',
      metaTitle: '',
      metaDescription: '',
      metaKeywords: ''
    });

    this.ogImage = null;
    this.ogImagePreview = '';
    this.ogImageError = '';

    this.saveAnotherAfterSubmit = false;
  }

  private clearOgImage(
    input: HTMLInputElement
  ): void {
    input.value = '';
    this.ogImage = null;
    this.ogImagePreview = '';
  }
}
